import { useEffect, useRef } from 'react';

/**
 * ESC 分層(R393 T3;CIS 交互:「第二層視窗(如配音)開啟時,ESC 先關該層、不誤關底層」)
 * -----------------------------------------------------------------------------
 * 每個「ESC 會關掉的東西」(燈箱 / 彈框 / 抽屜 / 展開中的下拉)掛一層;一次 ESC 只交給
 * **最上層**(最後掛上、仍啟用中的那一層)。之前各自 `document.addEventListener('keydown')`
 * —— 燈箱上再開一個確認框,按一次 ESC 兩層一起關(FeedbackDeleteConfirm 就是這樣)。
 *
 * - `enabled = false` 的層**照樣佔著最上層**:輸入型燈箱刻意關掉 ESC(FB-20260828-0001)時,
 *   ESC 不可以「穿過它」去關底下那層。
 * - IME 組字中(`isComposing`)的 ESC 是取消組字,不是關窗。
 * - 已被元件自己處理掉的 ESC(`defaultPrevented`:例如燈箱內的行內改名框按 ESC = 取消編輯)
 *   不再往下關窗。React 的 onKeyDown 掛在 root 容器,一定比這支 document 監聽先跑。
 * - 不 `stopPropagation`:沒掛層的既有監聽照舊收得到(行為只比以前少「誤關底層」這一件)。
 */
interface Layer {
  onEscape: () => void;
  enabled: () => boolean;
}

const layers: Layer[] = [];
let installed = false;

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented || layers.length === 0) return;
  const top = layers[layers.length - 1];
  if (!top || !top.enabled()) return;
  top.onEscape();
}

function ensureInstalled(): void {
  if (installed || typeof document === 'undefined') return;
  document.addEventListener('keydown', onKeyDown);
  installed = true;
}

/**
 * 掛一層 ESC。`active = false` 時不佔層(例:下拉收合中)。
 * `onEscape` 走 ref 讀最新值(mount-once 的閉包會凍結初值 —— R393 T11 RC3 同族)。
 */
export function useEscapeLayer(onEscape: () => void, active = true, enabled = true): void {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (!active) return;
    ensureInstalled();
    const layer: Layer = {
      onEscape: () => onEscapeRef.current(),
      enabled: () => enabledRef.current,
    };
    layers.push(layer);
    return () => {
      const i = layers.lastIndexOf(layer);
      if (i >= 0) layers.splice(i, 1);
    };
  }, [active]);
}

/** 測試用:目前掛著幾層。 */
export function _escapeLayerCountForTest(): number {
  return layers.length;
}
